import React from 'react';
import { Button, Icon, Field, SelectInput, ConfirmModal } from '../../../components/ui';
import { del, moveGuardAssignment } from '../shared';

/*
 * GateGuardsPanel — the "Guards" tab for a selected GATE in the unified Setup pane.
 * Extracted verbatim from AssignmentsPane's gate detail: the roster (guards on the gate,
 * each removable), the "Assign a guard" picker (picking a guard who mans another gate MOVES
 * them, behind a confirm — single-gate-per-guard), and "+ New guard" (create-and-assign in
 * one step, delegated to the container which opens a pre-filled guard create form).
 *   assign/move/unassign via the shared create-first moveGuardAssignment helper.
 *   remove:  DELETE api/gateassignmentdestroy/:id
 *
 * Props: gate, guards, assignments, webToken, toast, refreshAssignments(), onNewGuard(gate).
 */
export default class GateGuardsPanel extends React.Component {
  state = { addGuardId: '', saving: false, deleting: false, confirm: null, confirmMove: null };

  componentDidUpdate(prevProps) {
    if (prevProps.gate && this.props.gate && prevProps.gate.id !== this.props.gate.id) {
      this.setState({ addGuardId: '', confirm: null, confirmMove: null });
    }
  }

  assignmentsByGate = () => {
    const map = {};
    (this.props.assignments || []).forEach((a) => { const gid = a.Gate ? a.Gate.id : a.GateId; if (gid == null) return; (map[gid] = map[gid] || []).push(a); });
    return map;
  };
  guardsOnGate = (gateId) => (this.assignmentsByGate()[gateId] || []);
  currentAssignmentFor = (guardId) => (this.props.assignments || []).find((a) => String(a.GateUserId) === String(guardId)) || null;
  addableGuards = (gate) => {
    const custId = gate.GateCustomer ? gate.GateCustomer.id : gate.GateCustomerId;
    const here = new Set(this.guardsOnGate(gate.id).map((a) => String(a.GateUserId)));
    return (this.props.guards || [])
      .filter((g) => g.GateCustomer && String(g.GateCustomer.id) === String(custId))
      .filter((g) => !here.has(String(g.id)));
  };

  assignGuard = async (gate) => {
    const guardId = this.state.addGuardId;
    if (!guardId) return;
    const existing = this.currentAssignmentFor(guardId);
    if (existing && String(existing.Gate ? existing.Gate.id : existing.GateId) !== String(gate.id)) { this.setState({ confirmMove: { gate, guardId, existing } }); return; }
    await this.doAssign(gate, guardId, existing);
  };
  doAssign = async (gate, guardId, existing) => {
    this.setState({ saving: true });
    try {
      await moveGuardAssignment({ gate, guardId, existingId: existing ? existing.id : null, webToken: this.props.webToken });
      this.setState({ saving: false, addGuardId: '', confirmMove: null });
      this.props.toast.success(existing ? 'Guard moved to this gate' : 'Guard assigned to this gate');
      if (this.props.refreshAssignments) await this.props.refreshAssignments();
    } catch (e) {
      console.error('Error:', e);
      this.setState({ saving: false, confirmMove: null });
      this.props.toast.error('Gate assignment failed. If the problem continues please contact your installer.');
    }
  };

  askRemove = (assignment) => this.setState({ confirm: { assignment } });
  doRemove = async () => {
    const a = this.state.confirm.assignment;
    this.setState({ deleting: true });
    try {
      await del('api/gateassignmentdestroy/' + a.id, this.props.webToken);
      this.setState({ deleting: false, confirm: null });
      this.props.toast.success('Guard removed from this gate');
      if (this.props.refreshAssignments) await this.props.refreshAssignments();
    } catch (e) {
      console.log('Gate assignment delete failed');
      this.setState({ deleting: false, confirm: null });
      this.props.toast.error('Remove failed. If the problem continues please contact your installer.');
    }
  };

  render() {
    const gate = this.props.gate;
    if (!gate) return null;
    const custName = gate.GateCustomer ? gate.GateCustomer.sName : '';
    const onGate = this.guardsOnGate(gate.id);
    const addable = this.addableGuards(gate);
    return (
      <div className="tabpane">
        <p className="sub" style={{ margin: '0 0 14px' }}>Assign &amp; reassign guards right here — no separate Gate Assignments screen.</p>

        <div className="section-label"><span className="eyebrow">Guards on this gate ({onGate.length})</span></div>
        {onGate.length === 0 ? (
          <div className="devnote" style={{ background: 'rgba(210, 153, 34, .14)', borderColor: 'rgba(210, 153, 34, .38)', color: 'var(--warn)' }}>
            <Icon name="warning" size={16} /><span>Unassigned — no guard is currently manning this gate.</span>
          </div>
        ) : (
          <div className="roster">
            {onGate.map((a) => (
              <div className="roster__row" key={a.id}>
                <Icon name="user" size={15} className="roster__ico" />
                <span className="roster__name grow truncate">{a.GateUser ? a.GateUser.sFullName : 'Guard'}</span>
                <Button size="sm" variant="default" onClick={() => this.askRemove(a)}>Remove</Button>
              </div>
            ))}
          </div>
        )}

        <div className="section-label"><span className="eyebrow">Assign a guard</span></div>
        <p className="sub" style={{ margin: '-4px 0 12px' }}>Add a guard to this gate. Picking a guard who mans another gate moves them here (reassign).</p>
        <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
          <Field label="Guard" full>
            <SelectInput value={this.state.addGuardId} onChange={(e) => this.setState({ addGuardId: e.target.value })} disabled={addable.length === 0}>
              <option value="">Select a guard…</option>
              {addable.map((g) => {
                const cur = this.currentAssignmentFor(g.id);
                const at = cur && cur.Gate ? ` — at ${cur.Gate.sName}` : '';
                return <option key={g.id} value={g.id}>{g.sFullName}{at}</option>;
              })}
            </SelectInput>
          </Field>
          <Button onClick={() => this.assignGuard(gate)} loading={this.state.saving} disabled={!this.state.addGuardId || this.state.saving}>Assign</Button>
          {this.props.onNewGuard && (
            <Button variant="default" icon={<Icon name="plus" size={14} />} onClick={() => this.props.onNewGuard(gate)}>New guard</Button>
          )}
        </div>
        {addable.length === 0 && (
          <p className="sub" style={{ marginTop: 8 }}>All guards in {custName || 'this customer'} are already on this gate. Use “New guard” to create and assign one.</p>
        )}

        {this.state.confirm && (
          <ConfirmModal open onClose={() => this.setState({ confirm: null })} onConfirm={this.doRemove} onCancel={() => this.setState({ confirm: null })}
            title="Remove this guard?"
            message={`Remove ${this.state.confirm.assignment.GateUser ? this.state.confirm.assignment.GateUser.sFullName : 'this guard'} from ${this.state.confirm.assignment.Gate ? this.state.confirm.assignment.Gate.sName : 'this gate'}. The guard stays in the system and can be reassigned.`}
            confirmLabel="Remove" danger loading={this.state.deleting} />
        )}
        {this.state.confirmMove && (
          <ConfirmModal open onClose={() => this.setState({ confirmMove: null })} onCancel={() => this.setState({ confirmMove: null })}
            onConfirm={() => { const m = this.state.confirmMove; this.doAssign(m.gate, m.guardId, m.existing); }}
            title="Move this guard?"
            message={`${this.state.confirmMove.existing.GateUser ? this.state.confirmMove.existing.GateUser.sFullName : 'This guard'} is currently manning ${this.state.confirmMove.existing.Gate ? this.state.confirmMove.existing.Gate.sName : 'their current gate'}. A guard can man only one gate, so assigning them to ${gate.sName} will MOVE them — their current gate becomes unassigned.`}
            confirmLabel="Move guard" loading={this.state.saving} />
        )}
      </div>
    );
  }
}
